import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, ExitPage, Field, Pill } from "./ExitParts";
import { dateFromToday, exitApi } from "./exitApi";

const PAGE_SIZE = 10;

// Local date: toISOString() is UTC and gives yesterday before 05:30 IST.
const today = () => dateFromToday(0);

// Date fields limited to today-30 .. today.
const LIMITED_DATES = ["date_resignation", "date_info_received"];

const PAST_DATE_MSG =
  "The selected date cannot be more than 30 days prior to the current date and future dates are not allowed.";

// Date of Leaving: only today-90 .. today+90 is selectable.
const LWD_MSG = "The Date of Leaving must be within 90 days before or after today.";

const SEPARATION_FIELDS = [
  {
    key: "exit_category",
    label: "Exit Category",
    type: "select",
    options: "exitCategory",
    required: true,
  },
  {
    key: "exit_type",
    label: "Exit Type",
    type: "select",
    options: "exitType",
    required: true,
  },
  {
    key: "date_resignation",
    label: "Date of Resignation",
    type: "date",
    required: true,
  },
  { key: "date_info_received", label: "Date Info Received", type: "date" },
  {
    key: "date_leaving",
    label: "Date of Leaving (LWD)",
    type: "date",
    required: true,
  },
  {
    key: "notice_after_confirmation",
    label: "Notice Period After Confirmation (days)",
    type: "select",
    options: "noticeDays",
    required: true,
  },
  {
    key: "notice_before_confirmation",
    label: "Notice Period Before Confirmation (days)",
    type: "select",
    options: "noticeDays",
    required: true,
  },
  {
    key: "working_status",
    label: "Working Status",
    type: "select",
    options: "employmentStatus",
    required: true,
  },
  {
    key: "exit_interview",
    label: "Exit Interview",
    type: "select",
    options: "yesNo",
    required: true,
  },
  {
    key: "exit_remark",
    label: "Exit Remark",
    type: "textarea",
    required: true,
  },
];

export default function ExitInitiate() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [options, setOptions] = useState({});
  const [vals, setVals] = useState({
    notice_after_confirmation: "30",
    notice_before_confirmation: "15",
    working_status: "On Notice Period",
    exit_interview: "Yes",
    date_resignation: today(),
    date_info_received: today(),
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    exitApi
      .organizations()
      .then((r) => {
        const org = r.data?.[0];
        if (org)
          exitApi
            .options(org.id)
            .then((o) => setOptions(o.data || {}))
            .catch(() => {});
      })
      .catch(() => {});
  }, []);

  // Reset to the first page whenever the search text changes.
  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => {
      exitApi
        .searchEmployees(search, page, PAGE_SIZE)
        .then((r) => {
          setResults(r.data || []);
          setTotal(r.total ?? (r.data || []).length);
        })
        .catch(() => {
          setResults([]);
          setTotal(0);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [search, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = results;

  // Voluntary Exit allows only Resignation; Involuntary Exit allows every other type.
  const exitTypes = (options.exitType || []).filter((t) =>
    vals.exit_category === "Voluntary Exit"
      ? t === "Resignation"
      : vals.exit_category === "Involuntary Exit"
        ? t !== "Resignation"
        : true,
  );

  const set = (k, v) => {
    // A new category can invalidate the chosen exit type, so clear it.
    setVals((x) => ({ ...x, [k]: v, ...(k === "exit_category" && { exit_type: "" }) }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const save = async () => {
    const e = {};
    SEPARATION_FIELDS.forEach((f) => {
      if (f.required && !vals[f.key]) e[f.key] = "Required";
    });
    // Backstop for typed dates; the calendar already restricts these via min/max.
    const earliest = dateFromToday(-30);
    const latest = dateFromToday(0);
    LIMITED_DATES.forEach((k) => {
      if (vals[k] && (vals[k] < earliest || vals[k] > latest))
        e[k] = PAST_DATE_MSG;
    });
    if (
      vals.date_leaving &&
      (vals.date_leaving < dateFromToday(-90) || vals.date_leaving > dateFromToday(90))
    )
      e.date_leaving = LWD_MSG;
    if (
      vals.date_leaving &&
      vals.date_resignation &&
      vals.date_leaving < vals.date_resignation
    )
      e.date_leaving = "Cannot be before the resignation date";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const separation = {
        ...vals,
        notice_after_confirmation: Number(vals.notice_after_confirmation),
        notice_before_confirmation: Number(vals.notice_before_confirmation),
        date_info_received: vals.date_info_received || null,
      };
      const res = await exitApi.createCase({
        employee_id: employee.id,
        separation,
      });
      toast.success(`${res.data.case_no} created.`);
      navigate(`/exit-clearance/${res.data.id}`);
    } catch (err) {
      const d = err.details?.separation || err.details || {};
      setErrors(
        Object.fromEntries(
          Object.entries(d).map(([k, v]) => [
            k,
            Array.isArray(v) ? v[0] : String(v),
          ]),
        ),
      );
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ExitPage
      title="Initiate Exit"
      actions={
        <button
          className="exitwf-btn ghost"
          onClick={() => navigate("/exit-clearance")}
        >
          Back to list
        </button>
      }
    >
      <Card title="Search Employee">
        <div className="exitwf-grid">
          <div className="exitwf-field wide">
            <label htmlFor="exf-emp-search">Employee Code / Name</label>
            <input
              id="exf-emp-search"
              className="form-control"
              autoFocus
              placeholder="Type code or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="exitwf-table-wrap">
          <table className="exitwf-table plain">
            <thead>
              <tr>
                <th>Employee Code</th>
                <th>Employee Name</th>
                <th>Project</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr
                  key={r.id}
                  className={employee?.id === r.id ? "selected" : ""}
                >
                  <td>{r.code}</td>
                  <td>{r.name}</td>
                  <td className="clip" title={r.project}>
                    {r.project}
                  </td>
                  <td>
                    {r.exit_in_progress ? (
                      <Pill kind="open">Exit in progress</Pill>
                    ) : (
                      <button
                        className="exitwf-btn sm"
                        onClick={() => setEmployee(r)}
                      >
                        Select
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!results.length && (
                <tr>
                  <td colSpan={4} className="empty">
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {total > PAGE_SIZE && (
          <div
            className="exitwf-actions"
            style={{ marginTop: 12, justifyContent: "space-between" }}
          >
            <span>
              Showing {pageStart + 1}–
              {pageStart + results.length} of{" "}
              {total}
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="exitwf-btn ghost sm"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="exitwf-btn ghost sm"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {employee && (
        <section className="exitwf-card panel">
          <div className="exitwf-panel-head">
            Separation Details — {employee.name} ({employee.code})
            <button
              className="x"
              aria-label="Close"
              onClick={() => setEmployee(null)}
            >
              ×
            </button>
          </div>
          <div className="exitwf-grid pad">
            {SEPARATION_FIELDS.map((f) => (
              <Field
                key={f.key}
                field={f}
                value={vals[f.key]}
                onChange={set}
                error={errors[f.key]}
                options={{ ...options, exitType: exitTypes }}
                readOnly={busy}
                min={LIMITED_DATES.includes(f.key) ? dateFromToday(-30) : f.key === "date_leaving" ? dateFromToday(-90) : undefined}
                max={LIMITED_DATES.includes(f.key) ? dateFromToday(0) : f.key === "date_leaving" ? dateFromToday(90) : undefined}
              />
            ))}
          </div>
          <div className="exitwf-actions pad">
            <button
              className="exitwf-btn ghost"
              onClick={() => setEmployee(null)}
            >
              Cancel
            </button>
            <button className="exitwf-btn" disabled={busy} onClick={save}>
              Save &amp; Initiate
            </button>
          </div>
        </section>
      )}
    </ExitPage>
  );
}
